import config from "../../config";
import { connect } from "react-redux";
import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
  lazy,
  Suspense,
} from "react";
import classNames from "classnames";
import { History } from "history";
import { IconX, IconPhoneCall } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router";

import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { FormattedMessage, useIntl } from "react-intl";

import { Dropdown } from "react-bootstrap";
import {
  IonPage,
  IonContent,
  IonToggle,
  IonSearchbar,
  IonIcon,
  useIonToast,
  IonSpinner,
} from "@ionic/react";
import EmptyConversationSvg from "../../theme/images/no-conversation-selected.svg";
import SparksSvg from "../../theme/images/sparks.svg";

// Icon
import { closeCircle } from "ionicons/icons";

// Validator
import { validateMessage } from "../../utils/validators";

import { setSendLoading } from "../../actions/send";
import {
  Contact,
  Settings,
  ChatInputRef,
  Filter,
  Message,
} from "../../interfaces";

//API
import ContactsApi from "../../api/contacts";

// Components
import ChatList from "../ChatList";
import ChatDetail from "../chat-detail";
import ContactDetails from "../ContactDetails";
import UploadPhotoModal from "../UploadPhotoModal";
import CustomToggle from "../CustomToggle";
import ChatInput from "../common/chat-input";
import FileInput from "../common/file-input";
import DropdownCheckbox from "../common/dropdown-checkbox";
import ContactPlatform from "../common/contact-platform";

import Call from "../common/call";
import EscalationAlert from "../common/escalation";

import useContactsStore from "../../store/contacts";
import {
  ContactData,
  useContactActions,
  useContactData,
} from "../../store/contact";
import { useSocketStore } from "../../store/socket";
import { useHandleNewMessage } from "../../hooks";
import { getChatId } from "../../utils/serialize";

import styles from "./styles.module.css";
import "./Chats.css";

const NewConversation = lazy(
  () => import("../new-conversation")
);

const isDev = config.ENV === "staging" || config.ENV === "local";

interface ILayoutProps {
  dispatch: Function;
  contacts: Contact[];
  escalatedCount: number;
  send: {
    body: string;
    loading: Boolean;
  };
  history: History;
  settings: Settings;
}

const Chats: React.FC<ILayoutProps> = (props: ILayoutProps) => {
  const { history, settings } = props;
  const { socketReady } = useSocketStore();

  const { setContactId, setContact, setContactPreview } = useContactActions();
  const contactData = useContactData();

  const {
    setFilters,
    setSearchResults,
    resetSearchResults,
    setIsSearching,
    searchActive,
    setSearchActive,
  } = useContactsStore();

  const filters = useContactsStore((state) => state.filters);
  const contactsDbObject = useContactsStore((state) => state.contactsDbObject);
  const escalatedCount = useContactsStore((state) => state.escalatedCount);
  const updateContactsDb = useContactsStore((state) => state.updateContactsDb);

  const [body, setBody] = useState("");
  const [scrollEscalations, setScrollEscalations] = useState(0);
  const [infoOpen, toggleInfoOpen] = useState(true);
  const [isOpenUpload, setIsOpenUpload] = useState(false);
  const [isLoadingUpload, setIsLoadingUpload] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [docFiles, setDocFiles] = useState<File[]>([]);
  const [isOpenDocsUpload, setIsDocsOpenUpload] = useState<boolean>(false);
  const [isOpenNewConv, setOpenNewConv] = useState<boolean>(false);
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const hiddenFileInput = useRef<HTMLInputElement | null>(null);
  const hiddenDocInput = useRef<HTMLInputElement | null>(null);
  const chatInputRef = React.useRef<ChatInputRef>(null);

  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);

  const chatId = useMemo(
    () => getChatId(history.location.pathname),
    [history.location.pathname]
  );

  const [present] = useIonToast();
  const intl = useIntl();

  // Contact detail event handler
  const handleDetail = useCallback(
    (c: Contact) => {
      console.log("setting up contact detail");
      setContact(c);
      updateContactsDb([c]);
    },
    [setContact, updateContactsDb]
  );

  const { data: contactInfo, isLoading: isRequestingContact } = useQuery({
    queryFn: () => ContactsApi.getContact(chatId),
    queryKey: ["getContact", chatId],
    refetchOnWindowFocus: false,
    enabled: !!chatId,
  });

  useEffect(() => {
    if (contactInfo && contactInfo?._id && !isRequestingContact) {
      handleDetail(contactInfo);
    }
  }, [contactInfo, isRequestingContact]);

  useHandleNewMessage();

  useEffect(() => {
    focusChatInput();
    setBody("");
    setReplyTo(null);
  }, [contactData.id]);

  useEffect(() => {
    if (contactData.messages.length) {
      const lastMessage = contactData.messages[contactData.messages.length - 1];
      if (lastMessage.id) {
        ContactsApi.readMessages(contactData.contact.id, lastMessage.id);
      }
    }
  }, [contactData.messages.length]);

  // Read the params from the URL and update the filters if they change
  useEffect(() => {
    const simpleParam = params.get("simple") ?? "";
    const companiesParam = params.get("companies") ?? "";

    const simpleFromUrl =
      simpleParam.trim() === ""
        ? []
        : simpleParam.split(",").map(s => s.trim()).filter(Boolean);

    const companiesFromUrl =
      companiesParam.trim() === ""
        ? []
        : companiesParam.split(",").map(s => s.trim()).filter(Boolean);

    const storeSimple = filters.simple.join(",");
    const storeCompanies = filters.companies.join(",");

    if (
      simpleFromUrl.join(",") !== storeSimple ||
      companiesFromUrl.join(",") !== storeCompanies
    ) {
      setFilters({ simple: simpleFromUrl, companies: companiesFromUrl });
    }
  }, [params]);

  // Update filters directly in the URL
  const onBeforeSetFilters = (newFilters: Filter) => {
    const params = new URLSearchParams();
    if (newFilters.simple.length) {
      params.set("simple", newFilters.simple.join(","));
    }
    if (newFilters.companies.length) {
      params.set("companies", newFilters.companies.join(","));
    }
    const url = `${history.location.pathname}?${params.toString()}`;
    history.replace(url);
  }

  const sendMessage = async (content: string) => {
    props.dispatch(setSendLoading(true));
    setBody("");
    setReplyTo(null);
    const { err } = await ContactsApi.postMessages(contactData.id, {
      content,
      replyTo: replyTo?.id,
    });
    props.dispatch(setSendLoading(false));
    if (err) {
      if (err.error) {
        return present({
          message: err.error?.[intl.locale],
          duration: 6000,
          color: "warning",
          position: "bottom",
        });
      }
      console.log(err);
    }
  };

  const sendPhotos = async (content: FormData) => {
    setIsLoadingUpload(true);
    const { err } = await ContactsApi.uploadMedia(contactData.id, content);
    setIsLoadingUpload(false);
    if (err) {
      if (err.error) {
        return present({
          message: err.error?.[intl.locale],
          duration: 6000,
          color: "warning",
          position: "bottom",
        });
      }
    }
    setIsOpenUpload(false);
    return onHandleModalClose();
  };

  const selectChat = useCallback(async (contactId: string) => {
    setContactId(contactId);
    if (contactsDbObject[contactId]) {
      setContactPreview(contactsDbObject[contactId] as ContactData);
    }
    // Set url filter params
    const params = new URLSearchParams();
    if (filters.simple.length) {
      params.set("simple", filters.simple.join(","));
    }
    if (filters.companies.length) {
      params.set("companies", filters.companies.join(","));
    }
    const url = `/chats/${contactId}?${params.toString()}`;
    history.replace(url);
    toggleInfoOpen(true);
  }, [setContactId, contactsDbObject, setContactPreview, history, toggleInfoOpen]);

  const enableOrDisableContact = async (enabled: boolean) => {
    const { err } = await ContactsApi.putEnabled(contactData.id, { enabled });
    if (err) {
      console.log(err);
    }
  };

  const solveEscalations = async (id: string) => {
    const { err } = await ContactsApi.putSolveEscalations(id);
    if (err) {
      console.log(err);
    }
  };

  const handleSendSubmit = async () => {
    const { err } = validateMessage(body);
    if (err) {
      return;
    }
    return sendMessage(body);
  };

  const handleInputChange = (body: string) => setBody(body);

  const handleAutoChange = (event: CustomEvent<{ checked: boolean }>) => {
    return enableOrDisableContact(event.detail.checked);
  };

  const toggleBlock = async (contact: Contact) => {
    const { id, blocked } = contact;

    const { err } = await ContactsApi.putBlocked(id, { blocked: !blocked });
    if (err) {
      console.log(err);
    }
  };

  const runConversational = async () => {
    try {
      const { err } = await ContactsApi.runConversational(contactData.id);
      if (err) {
        console.log(err);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleSearch = async (
    event: CustomEvent<{ value?: string | null | undefined }>
  ) => {
    if (event.detail.value) {
      setIsSearching(true);
      const { data, err } = await ContactsApi.search(
        event.detail.value,
        settings.companies
      );
      setIsSearching(false);
      if (data) {
        setSearchActive(true);
        setSearchResults(data.results);
      }
      if (err) {
        console.log(err);
        setSearchActive(false);
      }
    } else {
      resetSearchResults();
    }
  };

  const openSendPhoto = () => {
    hiddenFileInput.current?.click();
  };

  const openSendDoc = () => {
    hiddenDocInput.current?.click();
  };

  const scrollToEscalation = (messageId?: string) => {
    if (messageId) {
      const messageEl = document.getElementById(messageId);
      if (messageEl) {
        return messageEl.scrollIntoView({
          block: "center",
          behavior: "smooth",
        });
      }
    }
    return setScrollEscalations(scrollEscalations + 1);
  };

  const onHandleModalClose = () => {
    setIsDocsOpenUpload(() => false);
    setIsOpenUpload(() => false);

    if (hiddenFileInput?.current?.value) {
      hiddenFileInput.current.value = "";
    }
    if (hiddenDocInput.current?.value) {
      hiddenDocInput.current.value = "";
    }

    setFiles([]);
    return setDocFiles([]);
  };

  const onFilterChange = ({
    field,
    checked,
    type,
  }: {
    field: keyof Contact | string;
    checked: boolean;
    type: keyof Filter;
  }) => {
    const updatedFilter = checked
      ? {
          ...filters,
          [type]:
            type === "simple"
              ? [...filters.simple, field as keyof Contact]
              : [...filters.companies, field as string],
        }
      : {
          ...filters,
          [type]:
            type === "simple"
              ? filters.simple.filter((item) => item !== field)
              : filters.companies.filter((item) => item !== field),
        };

    onBeforeSetFilters(updatedFilter);
  };

  const onChangeFileInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "photo" | "doc"
  ) => {
    setIsLoadingUpload(false);
    const fileList = e.target.files;
    if (fileList && fileList.length > 0) {
      const files = [...fileList];

      if (type === "photo") {
        setFiles(files);
        return setIsOpenUpload(true);
      }

      setDocFiles(files);
      return setIsDocsOpenUpload(true);
    }
  };

  const createEscalation = async (contactId: string) => {
    await ContactsApi.escalateContact(contactId, {});
  };

  const focusChatInput = () => chatInputRef.current?.setInputFocus();

  return (
    <IonPage id="main-content">
      <IonContent className="ion-padding">
        <div className="page-wrapper">
          <div className="chat-wrapper-page">
            <div className="chat-list">
              <div className="chat-list-header">
                <div className="flex justify-content-between align-items-center gap-2 px-3 pt-3 mb-0">
                  <h1 className="fs-6 fw-semibold mb-0">Inbox</h1>
                  {settings.isWaReady && (
                    <>
                      <button
                        className={`${styles.newConvBtn} fs-3 fw-semibold-1`}
                        onClick={() => setOpenNewConv(true)}
                      >
                        <FormattedMessage id="common.newConversation" />
                      </button>
                      <Suspense fallback="Loading...">
                        <NewConversation
                          isOpen={isOpenNewConv}
                          onOpenChange={setOpenNewConv}
                          settings={settings}
                        />
                      </Suspense>
                    </>
                  )}
                </div>
                <div className="d-flex align-items-sm-center chat-list-header-search">
                  <IonSearchbar
                    className={classNames({
                      "search-is-active": searchActive === true,
                    })}
                    debounce={200}
                    onIonInput={(ev) => handleSearch(ev)}
                    placeholder={intl.formatMessage({
                      id: "chats.searchContact",
                    })}
                    mode={`ios`}
                    onIonFocus={() => {
                      setSearchActive(true);
                    }}
                    onIonClear={() => {
                      setIsSearching(false);
                      setSearchActive(false);
                      resetSearchResults();
                    }}
                  />
                  <div className="d-flex align-items-center">
                    <DropdownCheckbox
                      options={[
                        { id: "escalated" }, // contact.escalated true
                        { id: "manual" }, // contact.enabled false
                        { id: "book" }, // contact.actionsList ["availability", "book", "book_t", "book_listings"]

                        ...(isDev
                          ? [
                            { id: "payment_received"}, // contact.actionsList ["payment_received"]
                            { id: "outbound_conversion" }, // booking.conversion true
                            { id: "manual_conversation" }, // contact.ai.isManual true
                          ]
                          : []),
                      ]}
                      companies={settings.companies}
                      onFilterChange={onFilterChange}
                      filters={filters}
                      defaultClass="chats"
                    />
                    {filters.simple.length || filters.companies.length ? (
                      <IonIcon
                        style={{ opacity: "50%", marginLeft: "3px" }}
                        icon={closeCircle}
                        className="pointer"
                        size="small"
                        onClick={() =>
                          onBeforeSetFilters({ simple: [], companies: [] })
                        }
                      ></IonIcon>
                    ) : null}
                  </div>
                </div>
                <div
                  style={{
                    display: searchActive ? "none" : "flex",
                  }}
                  className="chat-list-header-review"
                >
                  {!socketReady ? (
                    <div className="connection-status fs-1 color-step-700 flex align-items-center ">
                      <IonSpinner
                        color="medium"
                        name="lines-sharp-small"
                      ></IonSpinner>{" "}
                      <FormattedMessage id="common.connecting" />
                    </div>
                  ) : (
                    <div className="form-check">
                      <input
                        className="form-check-input primary"
                        type="checkbox"
                        value=""
                        id="toggleOpen"
                        checked={filters.simple.includes("escalated")}
                        onChange={(e) => {
                          onBeforeSetFilters(
                            e.target.checked
                              ? {
                                  simple: ["escalated"],
                                  companies: filters.companies,
                                }
                              : { simple: [], companies: filters.companies }
                          );
                        }}
                      />

                      <label
                        htmlFor="toggleOpen"
                        className="form-check-label color-step-900 font-14 fw-semibold-1 pointer"
                      >
                        <FormattedMessage id="esc.needsReview" /> (
                        {escalatedCount})
                      </label>
                    </div>
                  )}
                </div>
              </div>
              <ChatList
                mode={"desktop"}
                select={selectChat}
                //changeStatus={changeStatusContact}
                isMulti={settings.multiAccount}
              />
            </div>

            {contactData.id ? (
              <div className="chat-presentation">
                <div className="chat-interface">
                  <div className="chat-interface-header">
                    <div className="navigation">
                      <div
                        className="chat-header-contact-info"
                        onClick={() => toggleInfoOpen(!infoOpen)}
                      >
                        <ContactPlatform
                          defaultClass="user-platform"
                          contact={contactData.contact}
                        />
                        {settings.multiAccount &&
                          contactData.companies.map((c) => (
                            <div className="user-company mr-1" key={c.id}>
                              <span className="badge text-bg-light">
                                {c.name}
                              </span>
                            </div>
                          ))}

                        <div className="user-info">
                          {contactData.contact.name}
                        </div>
                        {contactData.contact.escalated ? (
                          <span className="badge text-bg-warning">
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <div className="fw-semibold-1">
                                <FormattedMessage id="esc.needsReview" />
                              </div>
                            </div>
                          </span>
                        ) : null}
                      </div>
                      <div className="contact-tools">
                        <OverlayTrigger
                          placement="bottom"
                          delay={{ show: 100, hide: 1000 }}
                          overlay={
                            <Tooltip className="visito-tooltip">
                              <FormattedMessage id="chats.aiAuto" />
                            </Tooltip>
                          }
                        >
                          <IonToggle
                            checked={contactData.contact.enabled}
                            onIonChange={handleAutoChange}
                            mode={"ios"}
                            style={{ width: "max-content" }}
                            className="visito-toggle-conversation ios"
                          >
                            <img
                              src={SparksSvg}
                              width={23}
                              style={{ marginRight: "5px" }}
                              alt=""
                            ></img>
                          </IonToggle>
                        </OverlayTrigger>

                        {settings.isVoiceReady && isDev ? (
                          <div className="ml-1">
                            <Call
                              displayName={`${contactData.contact.name} ${contactData.contact.phoneNumber}`}
                              className="btn btn-visito-light btn-xs btn-flex"
                              icon={
                                <>
                                  <IconPhoneCall size={17} />
                                  <FormattedMessage id="aiSettingsCall" />
                                </>
                              }
                              onCall={() => console.log("calling")}
                              onHangUp={() => console.log("hangup")}
                              contactId={contactData.contact.id}
                              type={"outbound"}
                            />
                          </div>
                        ) : null}
                        {
                          <Dropdown style={{ marginLeft: "12px" }}>
                            <Dropdown.Toggle
                              id="dropdown-custom-components"
                              as={CustomToggle}
                              title="options"
                            >
                              Custom toggle
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item
                                onClick={() =>
                                  contactData.contact.escalated
                                    ? solveEscalations(
                                        contactData.contact.id
                                      )
                                    : createEscalation(
                                        contactData.contact.id
                                      )
                                }
                                eventKey="1"
                              >
                                {contactData.contact.escalated ? (
                                  <FormattedMessage id="chats.markAsClose" />
                                ) : (
                                  <FormattedMessage id="chats.markAsOpen" />
                                )}
                              </Dropdown.Item>

                              <Dropdown.Item
                                onClick={() =>
                                  toggleBlock(contactData.contact)
                                }
                                eventKey="2"
                              >
                                <FormattedMessage
                                  id={
                                    contactData.contact.blocked
                                      ? `chats.unblockContact`
                                      : `chats.blockContact`
                                  }
                                />
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        }
                      </div>
                    </div>
                  </div>
                  {contactData.contact.escalated && (
                    <EscalationAlert
                      escalation={contactData.escalations?.[0]}
                      solveEscalations={() =>
                        solveEscalations(contactData.contact.id)
                      }
                      scrollToEscalation={scrollToEscalation}
                    />
                  )}
                  <div className="chat-interface-body">
                    <ChatDetail
                      mode={"desktop"}
                      scrollEscalations={scrollEscalations}
                      replyTo={({ message }: { message: Message }) =>
                        setReplyTo(message)
                      }
                      actions={["reply", "flag", "clone", "debug"]}
                      isLoading={isRequestingContact}
                    />
                  </div>
                  <div className="chat-interface-footer">
                    <FileInput
                      ref={hiddenFileInput}
                      accept="image/png, image/jpeg"
                      multiple
                      onChange={(e) => onChangeFileInput(e, "photo")}
                      style={{ display: "none" }}
                    />
                    <FileInput
                      ref={hiddenDocInput}
                      accept="application/pdf, .pdf"
                      onChange={(e) => onChangeFileInput(e, "doc")}
                      style={{ display: "none" }}
                    />
                    <ChatInput
                      ref={chatInputRef}
                      id="send-body"
                      value={body}
                      placeholder={intl.formatMessage({
                        id: contactData.contact.blocked
                          ? "chats.blocked"
                          : "chats.newMessageInput",
                      })}
                      channel={contactData.contact.type}
                      onInputChange={handleInputChange}
                      onOpenPhotoInput={openSendPhoto}
                      onOpenDocInput={openSendDoc}
                      onRunConversational={runConversational}
                      onBeforeSubmit={handleSendSubmit}
                      disabled={contactData.contact.blocked}
                      replyTo={replyTo}
                      cleanReplyTo={() => setReplyTo(null)}
                    />
                  </div>
                </div>
                <div
                  className={classNames("chat-tools", {
                    open: infoOpen === true,
                  })}
                >
                  <div className="chat-tools-header">
                    <div
                      onClick={() => toggleInfoOpen(false)}
                      className="chat-tools-close"
                    >
                      <IconX />
                    </div>
                    <div>Info</div>
                  </div>
                  <div className="chat-tools-body">
                    <ContactDetails
                      toolCalls={contactData.toolCalls}
                      bookings={contactData.bookings}
                      contact={contactData.contact}
                      createEscalation={createEscalation}
                      solveEscalations={solveEscalations}
                      toggleBlock={toggleBlock}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="chat-welcome">
                <div className="welcome-content text-center">
                  <div className="row justify-content-center">
                    <div className="mb-3">
                      <img alt="" src={EmptyConversationSvg}></img>
                    </div>
                    <span className="color-step-900 font-14 fw-semibold-1">
                      <FormattedMessage id="chats.emptyWelcomeTitle" />
                    </span>
                    <span className="color-step-600 font-12">
                      <FormattedMessage id="chats.emptyWelcomeSubtitle" />
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <UploadPhotoModal
          show={isOpenUpload || isOpenDocsUpload}
          sending={isLoadingUpload}
          handleClose={onHandleModalClose}
          files={isOpenDocsUpload ? docFiles : files}
          upload={sendPhotos}
          mode={`desktop`}
          areDocs={isOpenDocsUpload}
          channel={contactData.contact.type}
        />
      </IonContent>
    </IonPage>
  );
};

export default connect((props: any) => ({
  send: props.send,
  settings: props.app.settings,
}))(Chats);
